import { BankAccountApi } from '@recargas-dominicanas/core/api';
import { BaseStore } from './BaseStore';

import type { 
  IBankAccount, 
  IBankAccountConfig, 
  IBankScrapingManager, 
  IDeposit, 
  IPage, 
  IWithdrawal, 
  IPendingTransaction 
} from '@recargas-dominicanas/core/types';

interface State {
  id: number;
  bankAccount: IBankAccount;
  deposits?: IPage<IDeposit>;
  withdrawals?: IPage<IWithdrawal>;
  pendingTransactions?: IPage<IPendingTransaction>;
  configs?: IBankAccountConfig[];
  bankScrapingManager?: IBankScrapingManager;
}

class Store extends BaseStore<State> {

  constructor() {
    super('bankAccounts');
  }

  getAll() {
    return this.findAll('bankAccount');
  }

  getById(id: number) {
    return this.findById(id, 'bankAccount');
  }

  getDeposits(id: number) {
    return this.findById(id, 'deposits');
  }

  getWithdrawals(id: number) {
    return this.findById(id, 'withdrawals');
  }

  getPendingTransactions(id: number) {
    return this.findById(id, 'pendingTransactions');
  }

  getLastBankScrapingManager(id: number) {
    return this.findById(id, 'bankScrapingManager');
  }

  getLastBankScrapingService(id: number) {
    return this.getLastBankScrapingManager(id)?.bankScrapingServices?.find(service => service.bankAccount.id === id);
  }

  getConfigs(id: number) {
    return this.findById(id, 'configs');
  }

  async fetchAll() {

    const bankAccounts = await BankAccountApi.getAll();

    bankAccounts.forEach(bankAccount => {

      if (this.exists(bankAccount.id)) {
        this.update(bankAccount.id, 'bankAccount', bankAccount);
        return;
      }

      this.add('bankAccount', bankAccount);
    });
  }

  async fetchById(id: number) {

    const bankAccount = await BankAccountApi.getById(id);

    if (this.exists(bankAccount.id)) {
      this.update(bankAccount.id, 'bankAccount', bankAccount);
      return;
    }

    this.add('bankAccount', bankAccount);
  }

  async fetchDeposits(id: number, page: number, size: number) {
    this.update(id, 'deposits', await BankAccountApi.getDeposits(id, page, size));
  }

  async fetchWithdrawals(id: number, page: number, size: number) {
    this.update(id, 'withdrawals', await BankAccountApi.getWithdrawals(id, page, size));
  }

  async fetchPendingTransactions(id: number, page: number, size: number) {
    this.update(id, 'pendingTransactions', await BankAccountApi.getPendingTransactions(id, page, size));
  }
  
  async fetchLastBankScrapingManager(id: number) {
    this.update(id, 'bankScrapingManager', await BankAccountApi.getLastBankScrapingManager(id));
  }

  async fetchConfigs(id: number) {
    this.update(id, 'configs', await BankAccountApi.getConfigs(id));
  }

  updateBankScrapingManager(id: number, bankScrapingManager: IBankScrapingManager) {
    this.update(id, 'bankScrapingManager', bankScrapingManager);
  }
}

export const BankAccountStore = new Store();
